import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["saveButton", "saveLabel", "changeCount", "errors", "addRowTemplate", "tableBody"]
  static values = { url: String, unsavedWarning: String }

  connect() {
    this.changeSet = new Map()
    this.originalValues = new Map()
    this.newRowCounter = 0

    this.element.querySelectorAll("tr[data-row-id]").forEach(row => {
      this.originalValues.set(row.dataset.rowId, this.#readFields(row))
    })

    this.#updateSaveButton()
    this._boundBeforeUnload = this.#beforeUnload.bind(this)
    window.addEventListener("beforeunload", this._boundBeforeUnload)
  }

  disconnect() {
    window.removeEventListener("beforeunload", this._boundBeforeUnload)
  }

  fieldChange(event) {
    const row = event.target.closest("tr[data-row-id]")
    if (!row) return

    const rowId = row.dataset.rowId

    if (row.dataset.newRow) {
      const fields = this.#readFields(row)
      this.changeSet.set(rowId, { operation: "create", ...fields })
      this.#updateSaveButton()
      return
    }

    const current = this.#readFields(row)
    const original = this.originalValues.get(rowId)

    if (this.#hasChanged(current, original)) {
      this.changeSet.set(rowId, { operation: "update", ...current })
      row.classList.add("table-warning")
    } else {
      this.changeSet.delete(rowId)
      row.classList.remove("table-warning")
    }

    this.#updateSaveButton()
  }

  deleteRow(event) {
    event.preventDefault()
    const row = event.target.closest("tr[data-row-id]")
    if (!row) return

    const rowId = row.dataset.rowId
    const button = event.currentTarget

    if (row.dataset.newRow) {
      this.changeSet.delete(rowId)
      row.remove()
      this.#updateSaveButton()
      return
    }

    if (row.dataset.markedForDeletion) {
      // Undo deletion
      delete row.dataset.markedForDeletion
      row.classList.remove("table-danger")
      row.querySelectorAll("input, textarea, select").forEach(el => { el.disabled = false })
      button.textContent = button.dataset.deleteLabel
      button.classList.replace("btn-outline-danger", "btn-danger")

      // Restore update state if the fields were modified before deletion
      const current = this.#readFields(row)
      const original = this.originalValues.get(rowId)
      if (this.#hasChanged(current, original)) {
        this.changeSet.set(rowId, { operation: "update", ...current })
        row.classList.add("table-warning")
      } else {
        this.changeSet.delete(rowId)
      }
    } else {
      // Mark for deletion using original provider values
      row.dataset.markedForDeletion = "true"
      row.classList.remove("table-warning")
      row.classList.add("table-danger")
      row.querySelectorAll("input, textarea, select").forEach(el => { el.disabled = true })
      button.dataset.deleteLabel = button.textContent
      button.textContent = this.element.dataset.dnsEditorUndoLabel || "Undo"
      button.classList.replace("btn-danger", "btn-outline-danger")

      const original = this.originalValues.get(rowId)
      this.changeSet.set(rowId, { operation: "delete", ...original })
    }

    this.#updateSaveButton()
  }

  addRow() {
    const template = this.addRowTemplateTarget
    const clone = template.content.cloneNode(true)
    const newRow = clone.querySelector("tr")
    const rowId = `new-${this.newRowCounter++}`

    newRow.dataset.rowId = rowId
    newRow.dataset.newRow = "true"
    newRow.classList.add("table-success")

    this.tableBodyTarget.appendChild(newRow)

    const nameInput = newRow.querySelector("[data-field='name']")
    if (nameInput) nameInput.focus()

    this.changeSet.set(rowId, { operation: "create", ...this.#readFields(newRow) })
    this.#updateSaveButton()
  }

  async save(event) {
    event.preventDefault()

    const changes = Array.from(this.changeSet.values())
    if (changes.length === 0) return

    this.#clearErrors()

    const button = this.saveButtonTarget
    const label = this.saveLabelTarget
    const originalLabel = label.textContent
    button.disabled = true
    label.textContent = "Saving…"

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content ?? ""
        },
        body: JSON.stringify({ changes })
      })

      let data
      try {
        data = await response.json()
      } catch {
        this.#showErrors(["Server error. Please try again."])
        label.textContent = originalLabel
        this.#updateSaveButton()
        return
      }

      if (data.success) {
        // Remove the beforeunload guard so the reload doesn't trigger it
        window.removeEventListener("beforeunload", this._boundBeforeUnload)
        window.location.reload()
      } else {
        this.#showErrors(data.errors || ["An unknown error occurred."])
        label.textContent = originalLabel
        this.#updateSaveButton()
      }
    } catch (error) {
      this.#showErrors([`Network error: ${error.message}`])
      label.textContent = originalLabel
      this.#updateSaveButton()
    }
  }

  #updateSaveButton() {
    const count = this.changeSet.size
    const button = this.saveButtonTarget
    const countEl = this.changeCountTarget

    button.disabled = count === 0
    countEl.textContent = count > 0 ? `(${count})` : ""
  }

  #readFields(row) {
    const fields = {}
    row.querySelectorAll("[data-field]").forEach(el => {
      fields[el.dataset.field] = el.value
    })
    return fields
  }

  #hasChanged(current, original) {
    if (!original) return true
    return Object.keys(current).some(key => current[key] !== (original[key] ?? ""))
  }

  #showErrors(errors) {
    const container = this.errorsTarget
    container.innerHTML = errors.map(e => `<div>${this.#escapeHtml(e)}</div>`).join("")
    container.classList.remove("d-none")
  }

  #clearErrors() {
    const container = this.errorsTarget
    container.innerHTML = ""
    container.classList.add("d-none")
  }

  #escapeHtml(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
  }

  #beforeUnload(event) {
    if (this.changeSet.size > 0) {
      event.preventDefault()
    }
  }
}
